/**
 * useRazorpay — lazily loads Razorpay's hosted checkout script and exposes a
 * typed `openCheckout`.
 *
 * The script (`checkout.js`) is injected on demand the first time a customer
 * reaches the payment screen, rather than in index.html, so it never costs the
 * rest of the app. It's loaded once per document and reused.
 *
 * We only type the slice of Razorpay's options/response we use. Card data is
 * collected entirely inside Razorpay's iframe — `handler` hands us back the
 * order/payment/signature triple, which the backend verifies.
 */
import * as React from 'react';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

/** The fields Razorpay returns to the `handler` on a successful payment. */
export interface RazorpaySuccess {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

export interface RazorpayCheckoutOptions {
  /** Public key id — comes from the `initiate` response, not an env var. */
  key: string;
  /** Razorpay order id from the `initiate` response. */
  order_id: string;
  /** Amount in paise (defensive — Razorpay derives it from the order too). */
  amount: number;
  currency: string;
  name: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  notes?: Record<string, string>;
  theme?: { color?: string };
  /** Called with the signed triple after a successful payment. */
  handler: (response: RazorpaySuccess) => void;
  modal?: { ondismiss?: () => void };
}

interface RazorpayInstance {
  open: () => void;
  on: (event: 'payment.failed', handler: (response: unknown) => void) => void;
}

type RazorpayConstructor = new (options: RazorpayCheckoutOptions) => RazorpayInstance;

declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}

let scriptPromise: Promise<void> | null = null;

/** Inject checkout.js once; resolve when `window.Razorpay` is available. */
function loadCheckoutScript(): Promise<void> {
  if (typeof window !== 'undefined' && window.Razorpay) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Razorpay checkout')));
      if (window.Razorpay) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = CHECKOUT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null; // allow a later retry
      reject(new Error('Failed to load Razorpay checkout'));
    };
    document.body.appendChild(script);
  });

  return scriptPromise;
}

export function useRazorpay() {
  const [isReady, setIsReady] = React.useState(
    typeof window !== 'undefined' && Boolean(window.Razorpay),
  );

  // Warm the script as soon as the hook mounts so checkout opens instantly.
  React.useEffect(() => {
    let cancelled = false;
    loadCheckoutScript()
      .then(() => { if (!cancelled) setIsReady(true); })
      .catch(() => { /* surfaced when openCheckout is called */ });
    return () => { cancelled = true; };
  }, []);

  /**
   * Ensures the script is loaded, then opens the checkout. Rejects if the
   * script can't load or Razorpay isn't available; the dismiss/failure paths
   * are reported through the options callbacks.
   */
  const openCheckout = React.useCallback(async (options: RazorpayCheckoutOptions) => {
    await loadCheckoutScript();
    if (!window.Razorpay) throw new Error('Razorpay checkout is unavailable');
    const rzp = new window.Razorpay(options);
    rzp.open();
    return rzp;
  }, []);

  return { isReady, openCheckout };
}
